import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import DocumentName from "documents/names/document-name";
import { z } from "zod";

export type DocumentNameJSON = z.infer<typeof DocumentNameSchema.DocumentNameJSONZ>;
export type DocumentNameParams = z.infer<typeof DocumentNameSchema.DocumentNameZ>;

export default class DocumentNameSchema {
  public static readonly DocumentNameJSONZ = z.string();
  public static readonly DocumentNameZ = z.custom<DocumentName>(
    (data) => data instanceof DocumentName,
    "Not a valid DocumentName instance"
  );

  public static validateJSON(values: unknown): DocumentNameParams {
    return parseOrThrow(
      DocumentNameSchema.DocumentNameZ,
      values,
      { 
        logError: true,
        schemaName: "DocumentNameSchema",
        operation: "validateJSON",
        payloadSample: values 
      }
    );
  }

  public static isValidJSON(values: unknown): values is DocumentNameParams {
    try {
      DocumentNameSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DocumentNameJSON): DocumentNameParams {
    const parsed = parseOrThrow(
      DocumentNameSchema.DocumentNameJSONZ,
      data,
      { 
        logError: true,
        schemaName: "DocumentNameSchema",
        operation: "deserialize",
        payloadSample: data 
      }
    );
    return DocumentNameSchema.codec.decode(parsed);
  }

  public static serialize(name: DocumentNameParams): DocumentNameJSON {
    const parsed = DocumentNameSchema.validateJSON(name);
    return DocumentNameSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    DocumentNameSchema.DocumentNameJSONZ,
    DocumentNameSchema.DocumentNameZ, {
    decode: (value) => new DocumentName(value),
    encode: (name) => name.value
  });
}
